import { Box, Button, IconButton, Stack, type SxProps, type Theme, Typography, useTheme } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { type ReactNode, useEffect, useRef, useState } from 'react';

interface DashboardWidgetProps {
  title?: string;
  titleError?: boolean;
  titleColor?: string;
  subtitle?: string;
  subtitleColor?: string;
  avatar?: ReactNode;
  action?: ReactNode;
  headerActions?: ReactNode;
  headerButtons?: Array<{
    icon: ReactNode;
    onClick: () => void;
    ariaLabel?: string;
    label?: string;
    variant?: 'icon' | 'button';
    active?: boolean;
    disabled?: boolean;
    sx?: SxProps<Theme>;
  }>;
  children: ReactNode;
  footer?: ReactNode;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
}

export default function DashboardWidget({
  title,
  titleError,
  titleColor,
  subtitle,
  subtitleColor,
  avatar,
  action,
  headerActions,
  headerButtons,
  children,
  footer,
  sx,
  contentSx,
}: DashboardWidgetProps) {
  const theme = useTheme();
  const contentRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const hasHeader = title || subtitle || avatar || action || headerActions || headerButtons?.length;

  useEffect(() => {
    const contentBox = contentRef.current;
    if (!contentBox) return;

    const checkScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = contentBox;
      const isScrollable = scrollHeight > clientHeight;

      setShowTopFade(isScrollable && scrollTop > 0);
      setShowBottomFade(isScrollable && scrollTop < scrollHeight - clientHeight - 1);
    };

    // Check on mount and when content changes
    checkScroll();

    // Check on scroll
    contentBox.addEventListener('scroll', checkScroll);

    // Check on resize
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(contentBox);

    return () => {
      contentBox.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, [children]);

  return (
    <Box
      sx={{
        'bgcolor': 'paper',
        'borderRadius': 2,
        'border': '1px solid',
        'borderColor': 'divider',
        'boxShadow': '0 1px 3px rgba(0, 0, 0, 0.05)',
        'transition': 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
        },
        'display': 'flex',
        'flexDirection': 'column',
        'gap': 2,
        'overflow': 'hidden',
        'minHeight': 0,
        'padding': 1,
        ...sx,
      }}
    >
      {hasHeader && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            minHeight: 40,
            maxHeight: 64, // Allow for up to 2 lines of text (lineHeight 1.4 * 2 + padding)
          }}
        >
          {/* Card header: Avatar, Title, Subtitle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {avatar && <Box sx={{ flexShrink: 0 }}>{avatar}</Box>}
            {titleError && <NotificationsActive sx={{ color: 'error.main', flexShrink: 0, fontSize: 20 }} />}
            {(title || subtitle) && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {title && (
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: titleColor ?? (titleError ? 'error.main' : 'text.primary'),
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: subtitleColor ?? 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            )}
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>

          {/* Header actions and buttons */}
          {(headerActions || headerButtons?.length) && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              {headerActions}
              {headerButtons?.map((button, index) => {
                const isButton = button.variant === 'button' || Boolean(button.label);
                if (isButton) {
                  return (
                    <Button
                      key={`header-button-${index}`}
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={button.onClick}
                      disabled={button.disabled}
                      startIcon={button.icon}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: 2,
                        px: 2,
                        py: 0.75,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        ...button.sx,
                      }}
                    >
                      {button.label}
                    </Button>
                  );
                }

                return (
                  <IconButton
                    key={`header-icon-${index}`}
                    size="small"
                    aria-label={button.ariaLabel}
                    onClick={button.onClick}
                    disabled={button.disabled}
                    sx={{
                      color: button.active ? 'primary.main' : 'text.secondary',
                      border: '1px solid',
                      borderColor: button.active ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      ...button.sx,
                    }}
                  >
                    {button.icon}
                  </IconButton>
                );
              })}
            </Stack>
          )}
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {showTopFade && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 40,
              background: `linear-gradient(to bottom, ${theme.palette.background.paper}, transparent)`,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}
        <Box
          ref={contentRef}
          sx={[
            {
              px: 1,
              py: 2,
              pt: 0,
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            },
            ...(Array.isArray(contentSx) ? contentSx : [contentSx]),
          ]}
        >
          {children}
        </Box>
        {showBottomFade && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 64,
              background: `linear-gradient(to top, ${theme.palette.background.paper}, transparent)`,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}
      </Box>
      {footer ? (
        <Box
          sx={{
            flexShrink: 0,
            px: 1,
            pb: 0.5,
            pt: 0.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}
